import React, { useEffect, useState } from 'react';
import {
  Card,
  Typography,
  Button,
  Space,
  message,
  Row,
  Col,
  Tabs,
  Spin,
  Input,
  Modal,
  Progress,
} from 'antd';
import {
  ArrowLeftOutlined,
} from '@ant-design/icons';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Dispatch,
  DispatchCheckin,
  DispatchCheckout,
  DispatchCollect,
  DispatchReturn,
  DispatchReview,
  StatusLog,
} from '../services/dispatches';
import customerApi from '../services/customerApi';
import { customerAuthService } from '../services/customerAuth';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { TabPane } = Tabs;

export const CustomerDispatchDetail: React.FC = () => {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [dispatch, setDispatch] = useState<Dispatch | null>(null);
  const [reviewModalVisible, setReviewModalVisible] = useState(false);
  const [reviewText, setReviewText] = useState('');
  const [submittingReview, setSubmittingReview] = useState(false);
  const customer = customerAuthService.getCurrentCustomer();
  const activeTab = searchParams.get('tab') || 'overview';

  useEffect(() => {
    if (id) {
      loadDispatch(parseInt(id, 10));
    }
  }, [id]);

  const loadDispatch = async (dispatchId: number) => {
    setLoading(true);
    try {
      const response = await customerApi.get(`/customer/dispatches/${dispatchId}`);
      setDispatch(response.data.data || response.data);
    } catch (error: any) {
      console.error('Failed to load dispatch:', error);
      let errorMessage = 'Failed to load dispatch';
      
      if (error.response) {
        errorMessage = error.response.data?.message || error.response.data?.error || `Server error: ${error.response.status}`;
        if (error.response.status === 401) {
          errorMessage = 'Unauthorized. Please login again.';
        } else if (error.response.status === 403) {
          errorMessage = 'Access denied.';
        } else if (error.response.status === 404) {
          errorMessage = 'Dispatch not found.';
        }
      } else if (error.request) {
        errorMessage = 'Network error: Unable to connect to server';
      } else {
        errorMessage = error.message || 'Unknown error occurred';
      }
      
      message.error(errorMessage);
      navigate('/customer/personal');
    } finally {
      setLoading(false);
    }
  };

  const formatDispatchNumber = (dispatchId: number) => {
    return `#${dispatchId.toString().padStart(7, '0')}`;
  };

  const formatDateTime = (dateString?: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    const hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const ampm = hours >= 12 ? 'pm' : 'am';
    const displayHours = hours % 12 || 12;
    return `${day}/${month}/${year} ${displayHours}:${minutes}${ampm}`;
  };

  const handleTabChange = (key: string) => {
    setSearchParams({ tab: key });
  };

  const handleAcknowledge = async () => {
    if (!dispatch) return;
    
    if (!['Collect', 'Return'].includes(dispatch.status || '')) {
      message.error('Dispatch can only be acknowledged when status is Collect or Return');
      return;
    }

    try {
      await customerApi.patch(`/customer/dispatches/${dispatch.id}/acknowledge`);
      message.success('Dispatch acknowledged successfully');
      loadDispatch(dispatch.id);
    } catch (error: any) {
      console.error('Failed to acknowledge dispatch:', error);
      let errorMessage = 'Failed to acknowledge dispatch';
      
      if (error.response) {
        errorMessage = error.response.data?.message || error.response.data?.error || `Server error: ${error.response.status}`;
      } else if (error.request) {
        errorMessage = 'Network error: Unable to connect to server';
      } else {
        errorMessage = error.message || 'Unknown error occurred';
      }
      
      message.error(errorMessage);
    }
  };

  const handleReviewSubmit = async (fromModal: boolean = false) => {
    if (!dispatch || !reviewText.trim()) {
      message.error('Please enter a review');
      return;
    }

    setSubmittingReview(true);
    try {
      await customerApi.post('/customer/dispatch-reviews', {
        dispatch_id: dispatch.id,
        customer_id: customer?.id,
        review: reviewText,
      });
      
      message.success('Review submitted successfully');
      if (fromModal) {
        setReviewModalVisible(false);
      }
      setReviewText('');
      loadDispatch(dispatch.id);
    } catch (error: any) {
      console.error('Failed to submit review:', error);
      let errorMessage = 'Failed to submit review';
      
      if (error.response) {
        errorMessage = error.response.data?.message || error.response.data?.error || `Server error: ${error.response.status}`;
      } else if (error.request) {
        errorMessage = 'Network error: Unable to connect to server';
      } else {
        errorMessage = error.message || 'Unknown error occurred';
      }
      
      message.error(errorMessage);
    } finally {
      setSubmittingReview(false);
    }
  };

  if (loading || !dispatch) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
        </div>
    );
  }

  const checkins = dispatch.checkins || [];
  const checkouts = dispatch.checkouts || [];
  const collects = dispatch.collects || [];
  const returns = dispatch.returns || [];
  const reviews = dispatch.reviews || [];
  const checklistPercent = (() => {
    const total = checkins.length + checkouts.length;
    if (total === 0) return 0;
    const completed = checkins.filter((item: DispatchCheckin) => item.enabled).length
      + checkouts.filter((item: DispatchCheckout) => item.enabled).length;
    return Math.round((completed / total) * 100);
  })();

  const getDispatchDisplayName = () => {
    return formatDispatchNumber(dispatch.id);
  };

  const mapStatus = (status: string | undefined): 'Pending' | 'Collect' | 'Return' => {
    if (!status) return 'Pending';
    const normalized = status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
    if (['Pending', 'Collect', 'Return'].includes(normalized)) {
      return normalized as 'Pending' | 'Collect' | 'Return';
    }
    return 'Pending';
  };

  const currentStatus = mapStatus(dispatch.status);

  const statuses: Array<'Pending' | 'Collect' | 'Return'> = ['Pending', 'Collect', 'Return'];
  const statusConfig = {
    Pending: { bg: '#E6F7FF', border: '#91D5FF', color: '#1890ff', dot: '#1890ff', label: 'Pending' },
    Collect: { bg: '#F6FFED', border: '#73D13D', color: '#52C41A', dot: '#52C41A', label: 'Collect' },
    Return: { bg: '#FFF7E6', border: '#FFA940', color: '#FF8C00', dot: '#FF8C00', label: 'Return' },
  };

  return (
    <div>
      {/* Header */}
      <div style={{ background: '#fff', padding: '16px 24px', borderRadius: 8 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <Title level={3} style={{ margin: 0 }}>
            {getDispatchDisplayName()}
          </Title>
          <Space>
            {['Collect', 'Return'].includes(dispatch.status || '') && (() => {
              // Check if there's already an acknowledge record for the current status
              const hasAcknowledgeRecord = dispatch.acknowledge_logs?.some((log: StatusLog) => {
                if (dispatch.status === 'Collect') {
                  return log.to_status === 'Acknowledge (collect)';
                } else if (dispatch.status === 'Return') {
                  return log.to_status === 'Acknowledge (return)';
                }
                return false;
              });
              
              // Only show button if no acknowledge record exists for current status
              if (!hasAcknowledgeRecord) {
                return (
                  <Button
                    type="primary"
                    onClick={handleAcknowledge}
                  >
                    {dispatch.status === 'Collect' ? 'Collect Acknowledge' : 'Return Acknowledge'}
                  </Button>
                );
              }
              return null;
            })()}
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/customer/personal')}
        >
          Back
        </Button>
          </Space>
        </div>
      </div>

      {/* Tabs */}
      <Card style={{ background: '#fff' }}>
        <Tabs 
          activeKey={activeTab}
          onChange={handleTabChange}
          tabBarExtraContent={
            <Space size={8}>
              {statuses.map((status) => {
                const isActive = currentStatus === status;
                const config = statusConfig[status];
                return (
                  <div
                    key={status}
                    style={{
                      padding: '4px 12px',
                      borderRadius: '12px',
                      backgroundColor: isActive ? config.bg : '#f5f5f5',
                      border: isActive ? `1px solid ${config.border}` : '1px solid #d9d9d9',
                      color: isActive ? config.color : '#595959',
                      fontWeight: isActive ? 500 : 400,
                      fontSize: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4,
                      transition: 'all 0.2s',
                    }}
                  >
                    <span style={{
                      width: 4,
                      height: 4,
                      borderRadius: '50%',
                      backgroundColor: isActive ? config.dot : '#8c8c8c',
                    }}></span>
                    {config.label}
                  </div>
                );
              })}
            </Space>
          }
        >
          {/* Overview Tab */}
          <TabPane tab="Overview" key="overview">
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 320 }}>
                <Card style={{ marginBottom: 8 }} bodyStyle={{ padding: 0 }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <tbody>
                      {/* Customer - 单独一行，合并后三列 */}
                      <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                        <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                          <strong>Customer</strong>
                        </td>
                        <td colSpan={3} style={{ padding: '12px', verticalAlign: 'top' }}>
                          {dispatch.customer?.name || '-'}
                        </td>
                      </tr>
                      {/* Type - 单独一行，合并后三列 */}
                      <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                        <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                          <strong>Type</strong>
                        </td>
                        <td colSpan={3} style={{ padding: '12px', verticalAlign: 'top' }}>
                          {dispatch.taskType?.name || '-'}
                        </td>
                      </tr>
                      {/* Year - 单独一行，合并后三列 */}
                      <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                        <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                          <strong>Year</strong>
                        </td>
                        <td colSpan={3} style={{ padding: '12px', verticalAlign: 'top' }}>
                          {dispatch.year || '-'}
                        </td>
                      </tr>
                      {/* Create - 单独一行，合并后三列 */}
                      <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                        <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                          <strong>Create</strong>
                        </td>
                        <td colSpan={3} style={{ padding: '12px', verticalAlign: 'top' }}>
                          {dispatch.create_date ? dayjs(dispatch.create_date).format('DD/MM/YYYY') : '-'}
                        </td>
                      </tr>
                      {/* Collect Due 和 Return Due - 同一行 */}
                      <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                        <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                          <strong>Collect Due</strong>
                        </td>
                        <td style={{ padding: '12px', verticalAlign: 'top' }}>
                          {dispatch.collect_due ? dayjs(dispatch.collect_due).format('DD/MM/YYYY') : '-'}
                        </td>
                        <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                          <strong>Return Due</strong>
                        </td>
                        <td style={{ padding: '12px', verticalAlign: 'top' }}>
                          {dispatch.return_due ? dayjs(dispatch.return_due).format('DD/MM/YYYY') : '-'}
                        </td>
                      </tr>
                      {/* Location - 单独一行，合并后三列 */}
                      <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                        <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                          <strong>Location</strong>
                        </td>
                        <td colSpan={3} style={{ padding: '12px', verticalAlign: 'top' }}>
                          {dispatch.location || '-'}
                        </td>
                      </tr>
                      {/* Team 和 Staff - 同一行 */}
                      <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                        <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                          <strong>Team</strong>
                        </td>
                        <td style={{ padding: '12px', verticalAlign: 'top' }}>
                          {dispatch.customer?.team?.name || '-'}
                        </td>
                        <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                          <strong>Staff</strong>
                        </td>
                        <td style={{ padding: '12px', verticalAlign: 'top' }}>
                          {dispatch.task?.staff?.name || dispatch.agent?.name || '-'}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </Card>
              </div>
              <Card style={{ width: 220, textAlign: 'center' }}>
                <Text type="secondary">Progress</Text>
                <div style={{ marginTop: 12 }}>
                  <Progress type="circle" percent={checklistPercent} width={96} />
                </div>
                <div style={{ marginTop: 8 }}>
                  <Text type="secondary">{checklistPercent}%</Text>
                </div>
              </Card>
            </div>
          </TabPane>

          {/* Photo Tab */}
          <TabPane tab="Photo" key="photo">
            <Row gutter={24}>
              <Col span={12}>
                <Card title="Collect" bodyStyle={{ padding: '16px' }}>
                  {collects.length === 0 ? (
                    <div style={{ color: '#999', textAlign: 'center', padding: '20px 0' }}>
                      No collects. Click 'Add' to add a collect.
                    </div>
                  ) : (
                    <div>
                      {collects.map((collect: DispatchCollect, index: number) => (
                        <div key={collect.id || index} style={{ marginBottom: 8 }}>
                          <Text>{collect.name}</Text>
                        </div>
                      ))}
                    </div>
                  )}
                </Card>
              </Col>
              <Col span={12}>
                <Card title="Return" bodyStyle={{ padding: '16px' }}>
                  {returns.length === 0 ? (
                    <div style={{ color: '#999', textAlign: 'center', padding: '20px 0' }}>
                      No returns. Click 'Add' to add a return.
                    </div>
                  ) : (
                    <div>
                      {returns.map((returnItem: DispatchReturn, index: number) => (
                        <div key={returnItem.id || index} style={{ marginBottom: 8 }}>
                          <Text>{returnItem.name}</Text>
                        </div>
                      ))}
                    </div>
                  )}
                </Card>
              </Col>
            </Row>
          </TabPane>


          {/* Review Tab */}
          <TabPane tab="Review" key="review">
            <Card title="Reviews" bodyStyle={{ padding: '16px' }}>
              {/* Customer Reviews */}
              <div style={{ marginBottom: 24 }}>
                <div style={{ marginBottom: 12, fontWeight: 500, fontSize: '16px' }}>Customer Reviews</div>
                <div style={{ marginBottom: 16 }}>
                  {reviews.filter((review: DispatchReview) => review.customer_id).map((review, index) => (
                    <div
                      key={review.id || index}
                      style={{
                        padding: '12px 0',
                        borderBottom: '1px solid #f0f0f0',
                      }}
                    >
                      <div style={{ marginBottom: 8 }}>
                        <Text>{review.review || '-'}</Text>
                      </div>
                      <div style={{ fontSize: '12px', color: '#999' }}>
                        By {review.customer?.name || 'Unknown'} • {formatDateTime(review.created_at)}
                      </div>
                    </div>
                  ))}
                  {reviews.filter((review: DispatchReview) => review.customer_id).length === 0 && (
                    <div style={{ color: '#999', textAlign: 'center', padding: '20px 0' }}>
                      No customer reviews submitted yet.
                    </div>
                  )}
                </div>
                <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #f0f0f0' }}>
                  <TextArea
                    rows={6}
                    placeholder="Enter review..."
                    value={reviewText}
                    onChange={(e) => setReviewText(e.target.value)}
                  />
                  <Button
                    type="primary"
                    onClick={() => handleReviewSubmit(false)}
                    loading={submittingReview}
                    disabled={!reviewText.trim()}
                    style={{ marginTop: 12 }}
                  >
                    Submit
                  </Button>
                </div>
              </div>

              {/* Staff Reviews */}
              <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 24, marginTop: 24 }}>
                <div style={{ marginBottom: 12, fontWeight: 500, fontSize: '16px' }}>Staff Reviews</div>
                <div style={{ marginBottom: 16 }}>
                  {reviews.filter((review: DispatchReview) => !review.customer_id).map((review, index) => (
                    <div
                      key={review.id || index}
                      style={{
                        padding: '12px 0',
                        borderBottom: '1px solid #f0f0f0',
                      }}
                    >
                      <div style={{ marginBottom: 8 }}>
                        <Text>{review.review || '-'}</Text>
                      </div>
                      <div style={{ fontSize: '12px', color: '#999' }}>
                        By {review.creator?.name || 'Unknown'} • {formatDateTime(review.created_at)}
                      </div>
                    </div>
                  ))}
                  {reviews.filter((review: DispatchReview) => !review.customer_id).length === 0 && (
                    <div style={{ color: '#999', textAlign: 'center', padding: '20px 0' }}>
                      No staff reviews yet.
                    </div>
                  )}
                </div>
              </div>
            </Card>
          </TabPane>
          </Tabs>
    </Card>

      {/* Review Modal */}
      <Modal
        title="Customer Review"
        open={reviewModalVisible}
        onOk={() => handleReviewSubmit(true)}
        onCancel={() => {
          setReviewModalVisible(false);
          setReviewText('');
        }}
        confirmLoading={submittingReview}
        okText="Submit"
        cancelText="Cancel"
      >
        <TextArea
          rows={6}
          placeholder="Enter your review..."
          value={reviewText}
          onChange={(e) => setReviewText(e.target.value)}
        />
      </Modal>
    </div>
  );
};
